import { type AuthLocation } from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import useCurrentSegmentIndex from 'components/VideoPlayer/hooks/useCurrentSegmentIndex';
import AuthService from 'models/AuthService';
import { useEffect, useState } from 'react';
import { type CallhomeCamera } from 'utils/CameraUtil';
import { type SegmentCacheResult } from 'utils/PlaylistCache/SegmentCache';
import useDevices from '../devices/useDevices';
import { useAuthDevice } from '../useAuthDevice';

export const BLANK_IMAGE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

/**
 * Returns a thumbnail for the specified camera, or undefined if the thumbnail
 * hasn't loaded yet. Returns null on error.
 */
export function useCameraThumbnail(
  camera: CallhomeCamera,
  streamId?: string,
  thumbnailName?: string,
  enabled = true,
) {
  const [thumbnail, setThumbnail] = useState<string | null | undefined>();

  const { devicesById } = useDevices();

  const device = devicesById[camera.deviceId ?? ''];

  const authLocation = useAuthDevice(device);

  const cameraId = camera.cameraId;

  useEffect(() => {
    if (!enabled) {
      return setThumbnail(undefined);
    }
    Promise.resolve()
      .then(async () => {
        const thumbnail = await getCameraThumbnail(cameraId, streamId, thumbnailName, authLocation);
        setThumbnail(thumbnail);
      })
      .catch((err) => {
        console.warn(`Error fetching thumbnail: ${err}`);
        setThumbnail(null);
      });
  }, [setThumbnail, cameraId, streamId, thumbnailName, authLocation, enabled]);

  return thumbnail;
}

export function getCameraThumbnail(
  cameraId: string,
  streamId: string | undefined,
  thumbnailName: string | undefined,
  authLocation: AuthLocation,
): Promise<string | undefined> {
  let url: string;
  if (streamId && thumbnailName) {
    url = `/cameras/${cameraId}` + `/streams/${streamId}` + `/thumb/${thumbnailName}`;
  } else {
    url = `/cameras/${cameraId}/thumb.jpg`;
  }

  return Promise.resolve().then(async () => {
    const fetchParams = AuthService.getLocalFetchParams(authLocation, url);
    const response = await fetch(...fetchParams);

    if (response.status < 200 || response.status >= 400) {
      throw new Error(`Unexpected response: ${response.status}`);
    }

    const imageData = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new window.FileReader();
      reader.readAsDataURL(imageData);
      reader.onload = () => resolve((reader.result as string | null) || undefined);
      reader.onerror = reject;
    });
  });
}

export function useCameraThumbnailAtTime(
  camera: PlayingCamera,
  motionSegmentsByCamera: SegmentCacheResult,
  playHeadTime: number,
  isLive: boolean,
  enabled = true,
) {
  const segmentsForCamera = motionSegmentsByCamera[camera.location.id]?.[camera.camera.cameraId];

  const currentSegment = segmentsForCamera?.find(
    (s) => s.start <= playHeadTime && s.end >= playHeadTime,
  );

  const hoverSegmentIndex = useCurrentSegmentIndex(currentSegment?.segments, playHeadTime);
  const segment = currentSegment?.segments[hoverSegmentIndex ?? -1];
  const thumbnailName = `${segment?.start}_${(segment?.end ?? 0) - (segment?.start ?? 0)}`;

  const thumbnail = useCameraThumbnail(
    camera.camera,
    isLive ? undefined : currentSegment?.streamId,
    isLive ? undefined : thumbnailName,
    enabled && (!!segment || isLive),
  );

  if (!thumbnail) {
    return BLANK_IMAGE;
  }
  return thumbnail;
}
